// Host-based Space switch (subdomain navigation). The active Space is derived
// by the backend from the host subdomain, so switching Space = navigating to
// that Space's subdomain host (root path). Works identically in dev
// (<sub>.localhost / <sub>.p1.local) and prod (<sub>.<APP_BASE_DOMAIN>).
//
// Pure + unit-testable. NOT a security mechanism - backend ACL stays the source
// of truth; the switcher only lists the caller's accessible Spaces.

import { isReservedHostLabel } from './reserved-hosts';

export interface SwitchLocation {
  protocol: string;
  hostname: string; // host WITHOUT port, e.g. "affilmaster.localhost"
  port: string;
}

export function spaceSwitchUrl(
  targetSubdomain: string | null | undefined,
  activeSubdomain: string | null | undefined,
  loc: SwitchLocation,
): string | null {
  if (!targetSubdomain) return null;

  const host = loc.hostname.toLowerCase();
  const target = targetSubdomain.toLowerCase();

  if (host === target || host.startsWith(target + '.')) return null;

  // Base = host minus the current leading label, when the host carries one we
  // should drop before prepending the target Space label:
  //   - the active Space subdomain (rondo./affilmaster.…), or
  //   - a reserved neutral label (login./www./api/…) - e.g. on login.<base>,
  //     where there is no active Space, we must still target <space>.<base>,
  //     NOT <space>.login.<base>.
  let base = host;
  const active = activeSubdomain?.toLowerCase();
  const firstLabel = host.split('.')[0] ?? '';
  if (active && host.startsWith(active + '.')) {
    base = host.slice(active.length + 1);
  } else if (host.includes('.') && isReservedHostLabel(firstLabel)) {
    base = host.slice(firstLabel.length + 1);
  }

  const targetHost = base ? `${target}.${base}` : target;
  const portSuffix = loc.port ? `:${loc.port}` : '';
  return `${loc.protocol}//${targetHost}${portSuffix}/`;
}
